/*
 * BNO freebies explore hotspots — DOM BUILDER.
 *
 * Used ONLY by sections/bno-freebies-explore.liquid. It builds the hotspot
 * overlay markup that the plugin PDP writes in Liquid, then hands off to the
 * shared driver (assets/bno-explore-hotspots.js), which is reused UNMODIFIED.
 *
 * WHY THIS EXISTS
 *   On the PDP the hotspots come from the product's bno.hotspots metafield,
 *   which is `type: json` — the PLATFORM parses it, so Liquid receives a real
 *   array and can loop it. On a page there is no product, so the source is a
 *   theme-editor `textarea` setting, which is just a string. Liquid has no JSON
 *   parser (`| json` serializes; there is no inverse filter), so the parse has
 *   to happen here.
 *
 *   That costs nothing: the overlay is already 100% JS-dependent. On the PDP it
 *   also ships inert — container [hidden], every dot [disabled] — and without
 *   the driver there are no working hotspots there either. Building the nodes
 *   client-side gives up no no-JS fallback.
 *
 * ORDER
 *   The section emits this file's <script type="module"> BEFORE the shared
 *   driver's. Module scripts execute in document order after parsing, so the
 *   overlay is in the DOM by the time the driver's querySelectorAll runs.
 *
 * OUTPUT
 *   Byte-for-byte the markup of sections/bno-plugin-explore.liquid (and the
 *   bundle modal in sections/bno-bundle-included.liquid): container [hidden],
 *   dots [disabled], the same class names, the same ARIA wiring. The driver
 *   drops both gates and takes over from there.
 *
 * DEGRADATION
 *   A malformed blob costs the OVERLAY: parse throws, or the result is not a
 *   non-empty array, and we insert nothing at all. No [data-bno-hotspots]
 *   container exists, so the driver's own `if (!containers.length) return`
 *   no-ops too — the screenshot renders clean, with no dead controls.
 *
 *   A malformed ENTRY costs one dot: an entry missing title, x or y is skipped;
 *   `position` falls back to "above" and `mobile` to false.
 *
 * THEME EDITOR
 *   The shared driver self-guards with window.__bnoHotspotsInit, so after a
 *   shopify:section:load it returns early and freshly built dots would stay
 *   [disabled]. Editing the JSON in the theme editor therefore needs a full
 *   preview reload, which the setting's `info` string says. Fixing that would
 *   mean touching the shared driver, which runs on all ten live plugin PDPs.
 */
(() => {
  const hosts = document.querySelectorAll('[data-bno-hotspots-host]');
  if (!hosts.length) return;

  // Accept a JSON number, or a numeric string (lenient, since the value is
  // hand-authored). Anything else — null, "", true, {} — is NaN and skips the
  // entry. Number('') is 0 and Number(null) is 0, hence the explicit guards.
  const num = (v) => {
    if (typeof v === 'number') return Number.isFinite(v) ? v : NaN;
    if (typeof v === 'string' && v.trim() !== '') return Number(v);
    return NaN;
  };

  // Author line breaks become <br>, matching newline_to_br in the Liquid
  // snippet. Text goes in as text nodes, so nothing needs escaping.
  const appendText = (parent, text) => {
    String(text)
      .split('\n')
      .forEach((line, i) => {
        if (i > 0) parent.appendChild(document.createElement('br'));
        if (line) parent.appendChild(document.createTextNode(line));
      });
  };

  // DOM-node mirror of snippets/bno-highlight-text.liquid: balanced **phrase**
  // pairs become <span class="bno-hl">, an ODD number of ** markers degrades to
  // plain text with the asterisks literal (never broken markup). split('**')
  // matches Liquid's split exactly — non-overlapping, left to right — and the
  // first segment is always OUTSIDE, so odd indices are the highlight runs.
  //
  // Note .bno-hotspot__body is already color: var(--bno-text-highlight) in
  // bno-shared.css, so .bno-hl is a visual no-op inside a bubble. Kept wired up
  // for parity with the PDP rather than for any effect.
  const fillBody = (el, raw) => {
    const text = String(raw);
    const parts = text.split('**');
    const markers = parts.length - 1;

    if (markers > 0 && markers % 2 === 0) {
      parts.forEach((part, i) => {
        if (i % 2 === 1) {
          const span = document.createElement('span');
          span.className = 'bno-hl';
          appendText(span, part);
          el.appendChild(span);
        } else {
          appendText(el, part);
        }
      });
      return;
    }

    appendText(el, text);
  };

  hosts.forEach((host) => {
    const src = host.querySelector('[data-bno-hotspots-src]');
    if (!src) return;

    let data;
    try {
      data = JSON.parse(src.textContent);
    } catch {
      return; // malformed blob -> no overlay
    }
    if (!Array.isArray(data) || !data.length) return;

    // The section id, so bubble ids stay unique if the section is ever placed
    // twice on one page.
    const sid = host.dataset.bnoHotspotsHost || '0';

    const container = document.createElement('div');
    container.className = 'bno-hotspots';
    container.setAttribute('data-bno-hotspots', '');
    container.hidden = true; // the driver drops this

    let built = 0;

    data.forEach((h, i) => {
      if (!h || typeof h !== 'object') return;

      const title = typeof h.title === 'string' ? h.title.trim() : '';
      const x = num(h.x);
      const y = num(h.y);
      if (!title || !Number.isFinite(x) || !Number.isFinite(y)) return;

      // Only 'below' is opted into; anything else (missing, misspelled) is
      // 'above'. The two [data-position] rules in bno-shared.css are the ONLY
      // source of the bubble's vertical placement, so an unrecognised value
      // must not reach the attribute.
      const position = h.position === 'below' ? 'below' : 'above';
      // JSON boolean, or the string the driver actually compares against.
      const mobile = h.mobile === true || h.mobile === 'true';

      const spot = document.createElement('div');
      spot.className = 'bno-hotspot';
      spot.dataset.position = position;
      spot.dataset.mobile = mobile ? 'true' : 'false';
      // Percentages of the rendered image box (the overlay is inset:0 on the
      // figure, which equals the image exactly), centred on the point by the
      // dot's own translate(-50%, -50%).
      spot.style.left = x + '%';
      spot.style.top = y + '%';

      // Indexed off the SOURCE position, so ids stay stable when entries above
      // are skipped. +1 to match the Liquid forloop.index the PDP uses.
      const bubbleId = 'BnoFreebiesHotspot-' + sid + '-' + (i + 1);

      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'bno-hotspot__dot';
      dot.setAttribute('aria-label', title);
      dot.setAttribute('aria-describedby', bubbleId);
      dot.setAttribute('aria-expanded', 'false');
      dot.disabled = true; // the driver drops this

      const pulse = document.createElement('span');
      pulse.className = 'bno-hotspot__pulse';
      pulse.setAttribute('aria-hidden', 'true');

      const mark = document.createElement('span');
      mark.className = 'bno-hotspot__mark';
      mark.setAttribute('aria-hidden', 'true');

      dot.append(pulse, mark);

      const bubble = document.createElement('span');
      bubble.className = 'bno-hotspot__bubble';
      bubble.id = bubbleId;
      bubble.setAttribute('role', 'tooltip');

      const titleEl = document.createElement('span');
      titleEl.className = 'bno-hotspot__title';
      titleEl.textContent = title;
      bubble.appendChild(titleEl);

      const bodyEl = document.createElement('span');
      bodyEl.className = 'bno-hotspot__body';
      if (typeof h.body === 'string' && h.body !== '') fillBody(bodyEl, h.body);
      bubble.appendChild(bodyEl);

      spot.append(dot, bubble);
      container.appendChild(spot);
      built += 1;
    });

    // Every entry was malformed: leave the image with no overlay at all rather
    // than an empty container.
    if (!built) return;

    host.appendChild(container);
  });
})();
